(ns app.front.ui.i18n
  (:require [app.front.ui :as ui]
            [app.front.ui.rich-text :as ui-rt]
            [app.front.util :as futil]
            [app.i18n.model :as i18n-model]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.i18n :as i18n]
            [core.front.icons :as i]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]))

(rf/reg-sub ::languages
  :<- [::i18n/locales]
  (fn [locales _]
    (->> locales keys (apply sorted-set))))

(rf/reg-sub ::language-names
  :<- [::i18n/locales]
  (fn [locales _]
    (->> locales
         (map (fn [[k v]] [k (:short-name v)]))
         (into {}))))

(rf/reg-sub ::language-override
  :-> ::language-override)

(rf/reg-sub ::editor-language
  (fn [[_ allowed-langs-sub]]
    [(rf/subscribe [allowed-langs-sub])
     (rf/subscribe [::language-override])
     (rf/subscribe [:language])]) ; see core.front.i18n
  (fn [[allowed-locales override language] _]
    ;; the edited language must be one of the allowed locales, so that e.g. a
    ;; French-only group gets its strings edited in French even when the UI
    ;; language is something else
    (or (get allowed-locales (or override language))
        (get allowed-locales i18n-model/default-lang)
        (first (sort allowed-locales)))))

;; Subscription for temporarily hiding language-selector
;; TODO: Genuinely multilingual :business/names & :business/trade-names
(rf/reg-sub ::default-language
  (fn [_ _] #{i18n-model/default-lang}))

(rf/reg-sub ::other-language
  :<- [::language-override]
  :<- [:language]
  (fn [[override language] _]
    (when (and override (not= override language))
      language)))

(rf/reg-event-db ::set-language
  (fn [db [_ lang]]
    (assoc db ::language-override lang)))

(defn update-val
  "Set the text for a language, dropping texts in languages that are not
  allowed. Texts in disallowed languages can appear in the edit state as
  display fallbacks for untranslated content (e.g. English content in a
  French-only group); editing must replace them instead of keeping stale
  copies around."
  [new-value editing-lang strings allowed-locales]
  (let [allowed+bases (->> allowed-locales
                           (mapcat (juxt identity i18n-model/get-base-lang))
                           set)]
    (-> (->> strings
             (filter (fn [[lang _]]
                       (or (allowed+bases lang)
                           (allowed+bases (i18n-model/get-base-lang lang)))))
             (into {}))
        (assoc editing-lang new-value))))

(defn get-actual-language-to-edit
  "When `strings` contains a language that matches a fallback for `language`,
  return that language for editing. E.g. if we have text for `:en` and want
  to edit `:en-us`, actually fall back to just editing `:en`. There should not
  be a need to specifically edit regional language variants in the contexts
  where localized-strings are used."
  [strings language]
  (let [langs (-> strings keys set)]
    (or (langs language)
        (langs (i18n-model/get-base-lang language))
        language)))

(defn localized-string-edit
  "Editor for localized strings. Use `edit-language-selector` to allow the user
  to choose which language is being edited.

  `:strings` must be the localized-string-entities set passed via `i18n-model/strings-map`
  `:on-blur` is triggered any time when exiting, `:on-end-edit` is similar but only triggers if
  there were changes"
  [{:keys [id strings on-change on-end-edit input-class label-text required multiline? allowed-locales-sub
           on-blur disabled light invalid placeholder auto-focus hide-label data-cy show-lang?]}]
  (r/with-let [*changed? (r/atom false)]
    (let [allowed     (<sub [allowed-locales-sub])
          language    (get-actual-language-to-edit
                        strings
                        (<sub [::editor-language allowed-locales-sub]))
          other-lang  (<sub [::other-language])
          on-end-edit (or (when (and on-end-edit @*changed?)
                            (fn [val]
                              (reset! *changed? false)
                              (on-end-edit val)))
                          on-blur)
          value       (get strings language)]
      (when language
        [(if multiline? ui/text-area ui/text-input)
         (-> {:type        :text
              :placeholder (get strings
                                other-lang
                                (some-> strings vals first))
              :label-text  (or (some-> label-text (cond-> show-lang? (str " (" (name language) ")")))
                               "")
              :hide-label  hide-label
              :auto-focus  (if (some? auto-focus)
                             auto-focus
                             true)
              :light       (if (some? light)
                             light
                             true)
              :invalid     (if (some? invalid)
                             invalid
                             (and required
                                  (str/blank? value)))
              :value       value
              :on-change   (fn [e]
                             (reset! *changed? true)
                             (-> e futil/tval (update-val language strings allowed) on-change))}
             (p/assoc-if :id id
                         :required required
                         :input-class input-class
                         :on-blur (when on-end-edit
                                    #(-> %
                                         futil/tval
                                         (update-val language strings allowed)
                                         on-end-edit))
                         :on-key-up (when on-end-edit
                                      (fn [e]
                                        (when (or (futil/enter-press? e)
                                                  (futil/esc-press? e))
                                          (-> e
                                              futil/tval
                                              (update-val language strings allowed)
                                              on-end-edit))))
                         :disabled disabled
                         :placeholder placeholder
                         :data-cy data-cy))]))))

(defn localized-rich-text-editor
  "Editor for localized rich text. Use `edit-language-selector` to allow the user
  to choose which language is being edited.

  `:strings` must be the localized-string-entities set passed via `i18n-model/strings-map`
  `:on-blur` is triggered any time when exiting, `:on-end-edit` is similar but only triggers if
  there were changes"
  [{:keys [id strings on-change on-end-edit allowed-locales-sub label-text required?
           on-blur button-style default-phrase-category phrases authority-group-id
           placeholder tr]}]
  (r/with-let [*changed? (r/atom false)]
    (let [allowed     (<sub [allowed-locales-sub])
          language    (get-actual-language-to-edit
                        strings
                        (<sub [::editor-language allowed-locales-sub]))
          on-end-edit (or (when (and on-end-edit @*changed?)
                            (fn [val]
                              (reset! *changed? false)
                              (on-end-edit val)))
                          on-blur)
          value       (language strings)]
      [ui-rt/editor-legacy (util/mmap {:id            (str id "-" language)
                                       :initial-value value
                                       :on-change     (when on-change
                                                        (fn [v]
                                                          (reset! *changed? true)
                                                          (-> v (update-val language strings allowed) on-change)))
                                       :on-blur       (when on-end-edit
                                                        #(-> %
                                                             (update-val language strings allowed)
                                                             on-end-edit))}
                                      default-phrase-category tr button-style phrases authority-group-id label-text
                                      placeholder required?)])))

(rf/reg-sub ::selectable-locales
  (fn [[_ allowed-locales-sub]]
    [(rf/subscribe [allowed-locales-sub])
     (rf/subscribe [::languages])])
  (fn [[allowed-locales languages] _]
    (filter (set allowed-locales) languages)))

(defn edit-language-selector
  "`:allowed-locales-sub` should be a subscription that evaluates to a set of locales that are used by the applicable
                          municipality. Use `::gas/allowed-locales` or `::ws-subs/allowed-locales`
                          depending on context.
   `:label-tr-key` custom translation key for label, defaults to :language/language
   `:class` CSS classes for the wrapper element
   `:dropdown-class` CSS class(es) as string for the dropdown element"
  [{:keys [allowed-locales-sub label-tr-key class dropdown-class light hide-label]}]
  (let [tr             (<sub [:tr])
        languages      (<sub [::i18n/locales])
        items          (<sub [::selectable-locales allowed-locales-sub])
        language-names (<sub [::language-names])]
    (when (-> items rest seq)
      [:div.bx--form-item.w-32.flex-none.items-stretch
       {:class class}
       (cc/dropdown (p/assoc-if
                      {:id                   "language-selector-id"
                       :items                items
                       :label                (tr [(or label-tr-key :language/language)])
                       :hide-label           (boolean hide-label)
                       :title-text           (tr [(or label-tr-key :language/language)])
                       :selected-item        (<sub [::editor-language allowed-locales-sub])
                       :item-to-string       #(get-in languages [(keyword %) :name])
                       :on-change            #(>evt [::set-language (-> % (futil/oget :selectedItem) keyword)])
                       :light                (boolean light)
                       :render-selected-item (fn [selected-item]
                                               (r/as-element
                                                 [:div.flex.flex-row.items-center.uppercase
                                                  (cc/icon {:icon        i/world
                                                            :aria-hidden true
                                                            :description ""
                                                            :class-name  "mr-2"
                                                            :width       "14"
                                                            :fill        "var(--ui-gray-30)"})
                                                  (get language-names (keyword selected-item))]))}
                      :class-name dropdown-class))])))
